/**
 * Scenario Portfolio — the what-if simulator. Pick a batch of not-yet-pledged contracts,
 * route them through a waterfall of conditions, see the effect on real Monitor/Capacity
 * numbers before anything is proposed, then raise a governed pledge decision + a CSV export.
 *
 * Backend: portfolio-risk-concentrations/app/router/scenario.py — read its module docstring
 * first. Two things carry over into how these hooks are shaped:
 *
 * ── "Real" here is NOT today's live Monitor/Capacity ──────────────────────────────────────
 * Scenario's "real" tiles exclude every contract still sitting under the scenario's source
 * investor code (a corrected count of what the book would total if not-yet-pledged contracts
 * didn't count yet) — live Monitor still counts them, because of a real, pre-existing
 * pipeline gap this feature works around rather than fixes. The two numbers will not match,
 * and every "real" label in this UI should read as "real, excluding not-yet-pledged
 * contracts" rather than "what Monitor shows today."
 *
 * ── Propose is a governed record + a CSV download, not an actual reassignment ────────────
 * business_performance_daily is read-only from this codebase. Approving a pledge proposal
 * records the decision; the CSV is what someone applies manually in the source system.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { getJson, postBlob, postJson, PRC_PREFIX } from "./client";
import type { ValueBasis } from "./concentration";

// ── Source picker ────────────────────────────────────────────────────────────────────────
// Every LIVE governed investor code (athena_input.investor_code_listing) -- the same table
// Manage Investors names codes from, NOT a scan of the latest BPD/bankdwn_daily snapshot.
// Deliberately kept in step with /scenario/contracts, which reads bankdwn_daily directly --
// a book-scanned list could name codes that table has never heard of, or miss ones it has.
export interface ScenarioInvestorCode {
  code: string;
  codeMapping: string | null;
  /** The facility(ies) this code is mapped to in Manage Capital, as recorded there (CAP1, CAP2, ABS SCENARIO, ...); normally exactly one.
   *  Empty when the code is not mapped to a facility, and a destination picker then shows none. */
  facilities: string[];
}

export function useScenarioInvestorCodes() {
  return useQuery({
    queryKey: ["prc-scenario-investor-codes"],
    queryFn: () => getJson<ScenarioInvestorCode[]>(`${PRC_PREFIX}scenario/investor-codes`),
  });
}

/** Every real, observed value for each condition-builder field that takes one of a fixed set
 *  of values -- riskRating, termMonths, state, assetType, paymentType, sicCode, vendorNumber.
 *  investorCode is not here -- it reuses useScenarioInvestorCodes() directly, the same
 *  governed list the destination picker already shows. */
export interface ScenarioFieldOptions {
  riskRating: string[];
  termMonths: number[];
  state: string[];
  assetType: string[];
  paymentType: string[];
  sicCode: string[];
  vendorNumber: string[];
}

export function useScenarioFieldOptions() {
  return useQuery({
    queryKey: ["prc-scenario-field-options"],
    queryFn: () => getJson<ScenarioFieldOptions>(`${PRC_PREFIX}scenario/field-options`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Contract-level listing ───────────────────────────────────────────────────────────────
export interface ScenarioContract {
  /** The identity of the contract schedule everywhere in this feature ("{contractNumber}_{scheduleNumber}"): what the waterfall
   *  matches on, what assignments and a proposal's contracts are keyed by. Stable from one daily snapshot to the next. */
  contractScheduleKey: string;
  /** The snapshot's own row key ("{contractNumber}_{scheduleNumber}_{fileDate}"): changes every day, so nothing matches on it. */
  masterKey: string;
  /** The real, human Contract Number, and the Schedule Number beside it: display these wherever a contract is shown to a person. */
  contractNumber: string | null;
  scheduleNumber: number | null;
  investorCode: string | null;
  dateBooked: string | null;
  /** Resolved from the raw residualCode via Manage Investors' "Manage Residual Codes" mapping
   *  (prc_eligibility_residual_codes) -- falls back to the CAP1/CAP2 book when a code has no
   *  mapping yet, so this is never null. */
  residualType: "Loan" | "Lease";
  /** The raw code (e.g. "$L", "FV") residualType above was resolved from -- display-only,
   *  not a waterfall dimension itself; residualType is. */
  residualCode: string | null;
  riskRating: string | null;
  termMonths: number | null;
  isBankrupt: boolean;
  netInvestmentDollars: number;
  /** "View contracts" table only -- distinct from netInvestmentDollars (the balance today),
   *  not itself a waterfall dimension. */
  financedAmount: number;
  contractType: "CAP 1" | "CAP 2";
  pgFlag: boolean;
  fico: number | null;
  state: string | null;
  assetType: string | null;
  sicCode: string | null;
  paymentType: string | null;
  vendorNumber: string | null;
  /** Friendly asset-industry label (e.g. "SOFTWARE") -- display-only, distinct from the raw
   *  assetType/"Asset Code" dimension used for waterfall matching. */
  assetIndustry: string | null;
}

export interface ScenarioContractsResult {
  /** The snapshot the contracts were read from: the effective date asked for, or the latest file before it when there is none that day. */
  fileDate: string | null;
  contracts: ScenarioContract[];
  dollarTotal: number;
  contractCount: number;
}

// ── Uploaded contract list (an alternative source pool) ──────────────────────────────────
/** A row of the upload the server did not accept, with why (the contract is under another code today, is not Active / Owned, has no such
 *  schedule, is not found, is a duplicate, or the row could not be read). `row` counts from 1 below the header. */
export interface ScenarioUploadIssue {
  row: number | null;
  contractNumber: string;
  /** the Schedule Number as the file had it */
  contractScheduleNumber: string;
  /** the Contract Schedule the server built from the Contract Number and Schedule Number ("20086710_1"); empty when it could not be */
  contractScheduleKey: string;
  investorCode: string;
  reason: string;
}

export interface ScenarioUploadResult {
  /** The snapshot the rows were checked against (see ScenarioContractsResult.fileDate). */
  fileDate: string | null;
  uploadedRows: number;
  /** The accepted contracts, in the shape /scenario/contracts returns. */
  contracts: ScenarioContract[];
  contractCount: number;
  /** Their total net investment: the booked value the user checks against their file. */
  dollarTotal: number;
  /** The distinct current investor codes among the accepted contracts, with how many each. */
  sourceInvestorCodes: Record<string, number>;
  /** The code, when there is exactly one; null when the file spans several (a scenario moves contracts out of ONE code). */
  sourceInvestorCode: string | null;
  /** Earliest and latest Date Booked of the accepted contracts: the effective window. */
  dateFrom: string | null;
  dateTo: string | null;
  issueCount: number;
  /** The first issues (capped); `issueCount` is the full number. */
  issues: ScenarioUploadIssue[];
}

export type UploadContractRow = { contractNumber: string; contractScheduleNumber: string; investorCode: string; contractScheduleKey?: string };

/** An approved UPLOAD scenario's pool, rebuilt from the contract list it saved: the same shape as an upload's answer, so Scenario Portfolio opens
 *  it exactly like an uploaded file. `fileDate` is the effective date to read it on (default: the one the scenario was built on). */
export function useOpenApprovedScenarioPool() {
  return useMutation({
    mutationFn: ({ scenarioRef, fileDate }: { scenarioRef: string; fileDate?: string }) =>
      getJson<ScenarioUploadResult>(
        `${PRC_PREFIX}scenario/approved/${encodeURIComponent(scenarioRef)}/pool${fileDate ? `?fileDate=${encodeURIComponent(fileDate)}` : ""}`,
      ),
  });
}

/** Looks an uploaded list of contract schedules up on the snapshot of the effective date `fileDate`: `rows` are
 *  {contractNumber, contractScheduleNumber, investorCode} as read from the file; the server joins the first two into the Contract Schedule. */
export function useUploadScenarioContracts() {
  return useMutation({
    mutationFn: ({ rows, fileDate }: { rows: UploadContractRow[]; fileDate: string }) =>
      postJson<ScenarioUploadResult>(`${PRC_PREFIX}scenario/upload-contracts`, { rows, fileDate }),
  });
}

/** Matches the backend's SCENARIO_CONTRACTS_PAGE_SIZE default (scenario.py). */
const SCENARIO_CONTRACTS_PAGE_SIZE = 5000;

/** How many pages to have in flight at once, once the total is known. Each request opens its
 *  own DB connection (this service is deliberately unpooled — see db.py's header), so this
 *  caps how many connections one page load can open at the same time rather than firing every
 *  remaining page at once for a truly huge date range. */
const SCENARIO_CONTRACTS_MAX_PARALLEL = 6;

interface ScenarioContractsPage extends ScenarioContractsResult {
  hasMore: boolean;
}

async function fetchScenarioContractsPage(
  params: { investorCode: string; dateFrom: string; dateTo: string; fileDate: string },
  offset: number,
): Promise<ScenarioContractsPage> {
  const q = new URLSearchParams({
    investorCode: params.investorCode,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    fileDate: params.fileDate,
    limit: String(SCENARIO_CONTRACTS_PAGE_SIZE),
    offset: String(offset),
  });
  return getJson<ScenarioContractsPage>(`${PRC_PREFIX}scenario/contracts?${q.toString()}`);
}

/**
 * A wide date range can mean tens of thousands of contracts — enough that fetching them in
 * one request took long enough over this service's per-request (unpooled) connection to trip
 * the gateway's timeout (see scenario.py's list_scenario_contracts docstring). Paginated,
 * limit/offset — so once the first page reports the total, every remaining page is independent
 * (unlike a cursor that depends on the page before it) and can be fetched in parallel rather
 * than one at a time, bounded by SCENARIO_CONTRACTS_MAX_PARALLEL. Assembles the full array
 * before returning, so every existing caller (Scenario Portfolio seeds draft.contracts from
 * this, and everything downstream — Apply, Monitor, Capacity, Propose, /scenario/waterfall's
 * matching — still gets the SAME complete contract list it always did) needs no change at all.
 */
export function useScenarioContracts(params: {
  investorCode: string;
  dateFrom: string;
  dateTo: string;
  /** The scenario's effective date (yyyy-MM-dd): the contracts are the ones in that day's snapshot. */
  fileDate: string;
} | null) {
  return useQuery({
    queryKey: ["prc-scenario-contracts", params],
    queryFn: async (): Promise<ScenarioContractsResult> => {
      const first = await fetchScenarioContractsPage(params!, 0);
      const pages: ScenarioContractsPage[] = [first];

      if (first.hasMore) {
        const offsets: number[] = [];
        for (
          let offset = SCENARIO_CONTRACTS_PAGE_SIZE;
          offset < first.contractCount;
          offset += SCENARIO_CONTRACTS_PAGE_SIZE
        ) {
          offsets.push(offset);
        }
        // Fixed-size batches, not one Promise.all over every remaining offset — see
        // SCENARIO_CONTRACTS_MAX_PARALLEL's comment.
        for (let i = 0; i < offsets.length; i += SCENARIO_CONTRACTS_MAX_PARALLEL) {
          const batch = offsets.slice(i, i + SCENARIO_CONTRACTS_MAX_PARALLEL);
          const results = await Promise.all(
            batch.map((offset) => fetchScenarioContractsPage(params!, offset)),
          );
          pages.push(...results);
        }
      }

      // `pages` is already in offset order: Promise.all resolves in the same order as the
      // offsets it was given regardless of which request actually finishes first, and each
      // batch is awaited (and pushed) before the next one starts.
      const contracts = pages.flatMap((p) => p.contracts);
      return {
        fileDate: first.fileDate,
        contracts,
        dollarTotal: first.dollarTotal,
        contractCount: first.contractCount,
      };
    },
    enabled: !!params,
  });
}

// ── Waterfall ─────────────────────────────────────────────────────────────────────────────
// The full dimension whitelist scenario.py's _FIELD_GETTERS recognizes. Fixed, backend-known
// list -- deliberately no "add a new dimension yourself" affordance anywhere in the UI; a
// dimension not in this list doesn't exist as a condition target.
export type ScenarioField =
  | "dateBooked"
  | "investorCode"
  | "bankruptcy"
  | "residualType"
  | "riskRating"
  | "termMonths"
  | "pgFlag"
  | "fico"
  | "state"
  | "assetType"
  | "paymentType"
  | "sicCode"
  | "vendorNumber";

export type ScenarioOperator = "EQ" | "NE" | "LT" | "LTE" | "GT" | "GTE";

export interface ScenarioClause {
  field: ScenarioField;
  operator: ScenarioOperator;
  value: string | number | boolean;
}

const _EQUALITY_OPS: ScenarioOperator[] = ["EQ", "NE"];
const _NUMERIC_OPS: ScenarioOperator[] = ["EQ", "NE", "LT", "LTE", "GT", "GTE"];

/** Per-dimension display metadata for the condition builder: label, value shape, and which
 *  operators make sense. Mirrors ManageLimitsPage.tsx's DIMENSIONS/defaultOperator()
 *  convention, generalized to a repeatable clause-row list instead of one triple per limit. */
export interface ScenarioDimensionMeta {
  field: ScenarioField;
  label: string;
  valueType: "text" | "number" | "date" | "boolean" | "enum";
  operators: ScenarioOperator[];
  enumValues?: string[];
}

// Widened back to all 13, per direct instruction -- restores the narrowing this session
// briefly applied (PG Flag/Bankruptcy/Risk Rating/Investor Code only). Every dimension
// scenario.py's _FIELD_GETTERS supports again.
export const DIMENSION_META: ScenarioDimensionMeta[] = [
  { field: "pgFlag", label: "PG Flag", valueType: "boolean", operators: ["EQ"] },
  { field: "bankruptcy", label: "Bankruptcy", valueType: "boolean", operators: ["EQ"] },
  { field: "riskRating", label: "Risk Rating", valueType: "text", operators: _EQUALITY_OPS },
  { field: "investorCode", label: "Investor Code", valueType: "text", operators: _EQUALITY_OPS },
  { field: "dateBooked", label: "Effective Date", valueType: "date", operators: _NUMERIC_OPS },
  { field: "residualType", label: "Residual Type", valueType: "enum", operators: ["EQ"], enumValues: ["Loan", "Lease"] },
  { field: "termMonths", label: "Term", valueType: "number", operators: _NUMERIC_OPS },
  { field: "fico", label: "FICO", valueType: "number", operators: _NUMERIC_OPS },
  { field: "state", label: "State", valueType: "text", operators: _EQUALITY_OPS },
  { field: "assetType", label: "Asset Code", valueType: "text", operators: _EQUALITY_OPS },
  { field: "paymentType", label: "Payment Frequency", valueType: "text", operators: _EQUALITY_OPS },
  { field: "sicCode", label: "Borrower Industry (SIC)", valueType: "text", operators: _EQUALITY_OPS },
  { field: "vendorNumber", label: "Vendor", valueType: "text", operators: _EQUALITY_OPS },
];

// A NEW scenario's conditions start with no filters. These three are used only when an earlier approved scenario that never saved its
// conditions is opened again (ScenarioPortfolioStep): the closest to what such a scenario ran with, alongside the destinations it was
// approved with.
export const DEFAULT_SCENARIO_CLAUSES: ScenarioClause[] = [
  { field: "pgFlag", operator: "EQ", value: false },
  { field: "bankruptcy", operator: "EQ", value: false },
  { field: "residualType", operator: "EQ", value: "Loan" },
];

export function dimensionMeta(field: ScenarioField): ScenarioDimensionMeta {
  return DIMENSION_META.find((d) => d.field === field) ?? DIMENSION_META[0];
}

export interface ScenarioDestination {
  investorCode: string;
  /** The destination code's facility from Manage Capital (CAP1, CAP2, ABS SCENARIO, ...): the book the Monitor and Capacity steps re-book into. */
  facilityCode: string;
}

export interface ScenarioCondition {
  clauses: ScenarioClause[];
  /** null means "Exclude". */
  destination: ScenarioDestination | null;
}

export const OPERATOR_LABEL: Record<ScenarioOperator, string> = {
  EQ: "=",
  NE: "≠",
  LT: "<",
  LTE: "≤",
  GT: ">",
  GTE: "≥",
};

function formatClauseValue(meta: ScenarioDimensionMeta, value: string | number | boolean): string {
  if (meta.valueType === "boolean") return value ? "Yes" : "No";
  return String(value);
}

/** One human-readable badge per clause (e.g. "Risk Rating = A"). A condition with none takes every contract still
 *  unassigned, so it reads as how many that is ("2456 contracts") when the caller knows the count, and as
 *  ["Any contract"] when it does not yet. Shared between ScenarioApplyStep's condition cards,
 *  ScenarioProposeStep's proposal cards and ScenarioPortfolioStep's condition lines, so a condition reads
 *  identically wherever it's shown. */
export function conditionSummary(condition: ScenarioCondition, contractCount?: number): string[] {
  if (!condition.clauses.length) {
    return [contractCount === undefined ? "Any contract" : `${contractCount} ${contractCount === 1 ? "contract" : "contracts"}`];
  }
  return condition.clauses.map((c) => {
    const meta = dimensionMeta(c.field);
    return `${meta.label} ${OPERATOR_LABEL[c.operator]} ${formatClauseValue(meta, c.value)}`;
  });
}

export interface ScenarioConditionResult {
  index: number;
  destination: ScenarioDestination | null;
  dollarTotal: number;
  contractCount: number;
  matchedKeys: string[];
}

export interface ScenarioWaterfallResult {
  conditions: ScenarioConditionResult[];
  remainder: { dollarTotal: number; contractCount: number; matchedKeys: string[] };
}

export function useRunScenarioWaterfall() {
  return useMutation({
    mutationFn: (input: { contracts: ScenarioContract[]; conditions: ScenarioCondition[] }) =>
      postJson<ScenarioWaterfallResult>(`${PRC_PREFIX}scenario/waterfall`, input),
  });
}

// ── Scenario Monitor / Capacity ──────────────────────────────────────────────────────────
export interface ScenarioLimitRow {
  limitId: string;
  facilityCode: string;
  /** "WAFRA" on every external (CAP1/CAP2) row -- external books are Wafra's by
   *  construction (limit_rows.py's DEFAULT_INVESTOR). Internal rows carry their own real
   *  investor. Already returned by /scenario/monitor and /scenario/capacity; this just
   *  types what was already there. */
  investor: string;
  categoryGroup: string;
  limitDesc: string;
  thresholdPct: number;
  currentDollarPct: number | null;
  /** The dollars counted against this limit (null: not measured). */
  currentDollars?: number | null;
  /** The dollars of new business the limit can still take; negative once it has gone past (the Capacity page's Headroom). Null: not measured. */
  headroomDollars?: number | null;
  status: "GREEN" | "AMBER" | "RED" | "NOT_MEASURED";
  direction: "APPROACHING" | "RECEDING" | "STABLE";
  trend: { date: string; dollarPct: number }[];
  measured: boolean;
}

/** One investor code holding active contracts: what it holds today and what it holds once the scenario's assignments are applied. A code
 *  that receives contracts but holds none today is listed too (current 0). A figure the chosen basis does not measure is null. */
export interface ScenarioCodeTile {
  code: string;
  /** The code's name in the investor listing, e.g. "Unassigned / Staging Pool". */
  name: string | null;
  currentDollars: number | null;
  scenarioDollars: number | null;
  /** The code's Active contracts today. */
  activeContracts: number;
  /** Its Active contracts once the scenario's assignments are applied. Absent on an older server. */
  scenarioContracts?: number;
}

/** A scenario's source investor codes travel as ONE comma-joined string ("10,52"): a scenario may take contracts from several codes. */
export function codeList(codes: string | null | undefined): string[] {
  return (codes ?? "").split(",").map((c) => c.trim()).filter(Boolean);
}

/** "Code 10" / "Codes 10, 52": the source codes as a person reads them. */
export function codesLabel(codes: string | null | undefined): string {
  const list = codeList(codes);
  return `${list.length > 1 ? "Codes" : "Code"} ${list.join(", ")}`;
}

export interface ScenarioMonitorResult {
  asOfDate: string;
  /** The Loans / Leases view these figures are for ("Loans" / "Leases"), or null for both. */
  product?: "Loans" | "Leases" | null;
  tiles: { label: string; realDollars: number; scenarioDollars: number; realContracts?: number; scenarioContracts?: number }[];
  /** One card per investor code with active contracts (and any code the scenario moves contracts into). Absent on an older server. */
  codeTiles?: ScenarioCodeTile[];
  rows: { cap1: ScenarioLimitRow[]; cap2: ScenarioLimitRow[]; internal: ScenarioLimitRow[] };
  realRows: { cap1: ScenarioLimitRow[]; cap2: ScenarioLimitRow[]; internal: ScenarioLimitRow[] };
  /** The rows of every facility the server knows (CAP1, CAP2 and any added in Manage Capital), by facility code. */
  byFacility?: Record<string, { rows: ScenarioLimitRow[]; realRows: ScenarioLimitRow[] }>;
  notes: string[];
}

export interface ScenarioBatchInput {
  /** The scenario's effective date (yyyy-MM-dd): the portfolio is read as of the latest data file on or before it. Omitted: the latest. */
  fileDate?: string;
  /** The scenario's source pool -- backend re-derives every master_key under this code and
   *  window itself (one fast, indexed query), rather than the client enumerating and
   *  transmitting all of them. A wide date range's full pool used to mean a ~1.4MB request
   *  body just to name contracts the backend could look up directly, and that payload,
   *  combined with the six real-vs-scenario limit passes /scenario/monitor and
   *  /scenario/capacity each run, was enough to occasionally exceed the platform-api
   *  gateway's 30s proxy timeout. One code, or several comma-joined ("10,52"). */
  sourceInvestorCode: string;
  dateFrom: string;
  dateTo: string;
  /** contractScheduleKey -> destination contract_type, or omit/null for unassigned. Still sent by the
   *  client -- these come from ITS OWN waterfall computation, which the backend has no other
   *  way to know, and are a much smaller subset than the full source pool. */
  assignments: Record<string, string>;
  /** The scenario's source pool when it started from an UPLOADED contract list: exactly these contracts, instead of every contract of the
   *  source code in the window. Omitted for a scenario built from a code and window. */
  sourceKeys?: string[];
  /** Scenario Monitor's Loans / Leases view (omitted: both): each limit shows that product's contribution, measured against the facility. */
  product?: "LOAN" | "LEASE";
  /** contractScheduleKey -> destination investor code, for every contract the waterfall gave a destination: what the investor-code cards on
   *  Scenario Monitor move between codes. */
  codeAssignments?: Record<string, string>;
  /** Which quantity to measure the scenario in. Omitted (net_investment / "Book Value") by
   *  default, same as /concentration and /capacity's own `valueBasis` -- see
   *  app/router/scenario.py's _SCENARIO_EXTERNAL_FUNCTIONS. */
  valueBasis?: ValueBasis;
}

export function useScenarioMonitor() {
  return useMutation({
    mutationFn: (input: ScenarioBatchInput) =>
      postJson<ScenarioMonitorResult>(`${PRC_PREFIX}scenario/monitor`, input),
  });
}

// ── Scenario Monitor / Capacity answers, kept by what they were asked ──────────────────────────────────
// An answer depends on the scenario (its source pool, its conditions and where each sends contracts), on its effective date (the snapshot
// it is computed on) and on the filters that make the server recompute (Loans / Leases, Measured in, the facility). Each distinct combination is asked once and kept here, so switching back to a
// combination already looked at, or coming back to the step from another, shows it at once with no request. The scenario's own part of
// the key (`scenarioSignature`) changes the moment a condition is added, edited or given another destination, so the old answers can no
// longer be reached; `useResetScenarioAnswers` also drops them outright.
const SCENARIO_ANSWERS_KEY = ["prc", "scenario", "answers"] as const;
const ANSWER_OPTIONS = { staleTime: Infinity, gcTime: 30 * 60 * 1000, retry: false } as const;

/** Everything about a scenario that its Monitor / Capacity answer depends on, as a string: the source pool (codes and window, or the
 *  uploaded list), and each condition's clauses and destination. Change any of them and it is a different scenario. */
export function scenarioSignature(s: {
  sourceInvestorCode: string;
  dateFrom: string;
  dateTo: string;
  sourceKeys: string[] | null;
  conditions: ScenarioCondition[];
}): string {
  const pool = s.sourceKeys ? [s.sourceKeys.length, s.sourceKeys[0], s.sourceKeys[s.sourceKeys.length - 1]] : null;
  return JSON.stringify([s.sourceInvestorCode, s.dateFrom, s.dateTo, pool, s.conditions.map((c) => [c.clauses, c.destination])]);
}

/** The Monitor answer for one filter combination. `buildInput` is only called when a request is actually needed (null: not ready yet).
 *  The previous combination's answer stays on screen while a new one loads (`isFetching` says so); a combination asked before is
 *  returned from the cache without a request. */
export function useScenarioMonitorAnswer(
  signature: string,
  asOfDate: string,
  product: "LOAN" | "LEASE" | undefined,
  valueBasis: ValueBasis,
  buildInput: (() => ScenarioBatchInput) | null,
) {
  return useQuery({
    queryKey: [...SCENARIO_ANSWERS_KEY, "monitor", signature, asOfDate, product ?? "ALL", valueBasis],
    queryFn: () => postJson<ScenarioMonitorResult>(`${PRC_PREFIX}scenario/monitor`, buildInput!()),
    enabled: buildInput !== null,
    placeholderData: keepPreviousData,
    ...ANSWER_OPTIONS,
  });
}

/** Drops every kept Monitor / Snapshot answer: call it whenever a condition is added, edited or given another destination, or a new
 *  scenario starts, so the next visit to those steps asks again. */
export function useResetScenarioAnswers() {
  const queryClient = useQueryClient();
  return () => queryClient.removeQueries({ queryKey: SCENARIO_ANSWERS_KEY });
}

// ── Scenario Snapshot: the two contract tables ───────────────────────────────────────────────────────────
// The Snapshot page's PRC Contract Detail (the contracts the limits were measured on) and PRC Ineligible Contracts (the ones they leave
// out), for a scenario: the effective date's snapshot with the scenario's moves applied (app/router/scenario_snapshot.py). The risk-limits
// table of the same page is the Monitor answer above, so it needs no request of its own.
export type ScenarioSnapshotKind = "contracts" | "ineligible";

export interface ScenarioSnapshotColumn {
  key: string;
  header: string;
}

export type ScenarioSnapshotRow = Record<string, string | number | null>;

export interface ScenarioSnapshotPage {
  kind: ScenarioSnapshotKind;
  label: string;
  /** The snapshot the rows come from: the effective date, or the latest data file before it when there is none that day. */
  asOfDate: string;
  /** Every row the filters match, not only this page's. */
  total: number;
  offset: number;
  limit: number;
  columns: ScenarioSnapshotColumn[];
  rows: ScenarioSnapshotRow[];
  notes: string[];
}

/** Rows per page of a Snapshot contract table. */
export const SNAPSHOT_PAGE_SIZE = 100;

/** Which book a Snapshot contract table is narrowed to ("ALL": every book), as the server spells it. */
export type ScenarioSnapshotFacility = "ALL" | "CAP1" | "CAP2";

/** One page of a Snapshot contract table, kept per scenario, date, filters and page like the Monitor's answers; the page on screen stays
 *  while the next one loads. `buildInput` is only called when a request is needed (null: not ready yet). */
export function useScenarioSnapshotPage(
  kind: ScenarioSnapshotKind,
  signature: string,
  asOfDate: string,
  facility: ScenarioSnapshotFacility,
  search: string,
  offset: number,
  buildInput: (() => ScenarioBatchInput) | null,
) {
  return useQuery({
    queryKey: [...SCENARIO_ANSWERS_KEY, "snapshot", kind, signature, asOfDate, facility, search, offset],
    queryFn: () =>
      postJson<ScenarioSnapshotPage>(`${PRC_PREFIX}scenario/snapshot/${kind}`, {
        ...buildInput!(),
        facility: facility === "ALL" ? undefined : facility,
        search: search || undefined,
        limit: SNAPSHOT_PAGE_SIZE,
        offset,
      }),
    enabled: buildInput !== null,
    placeholderData: keepPreviousData,
    ...ANSWER_OPTIONS,
  });
}

/** A failed blob request carries its JSON error as a Blob: this reads the `detail` out of it. */
async function blobErrorDetail(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const detail = JSON.parse(await data.text())?.detail;
      if (typeof detail === "string" && detail) return detail;
    } catch {
      // not JSON: fall through to the generic message
    }
  }
  return apiDetail(err, fallback);
}

/** Downloads a whole Snapshot contract table as a CSV. The server streams it from a cursor (the table is tens of thousands of rows);
 *  it is saved client-side, through axios so the Keycloak bearer is attached (see downloadScenarioPledgeProposal). `facility` narrows it
 *  as it narrows the table on screen. Throws an Error carrying the server's reason. */
export async function downloadScenarioSnapshot(
  kind: ScenarioSnapshotKind,
  input: ScenarioBatchInput,
  facility: ScenarioSnapshotFacility = "ALL",
): Promise<void> {
  let result: { blob: Blob; contentDisposition: string | undefined };
  try {
    result = await postBlob(`${PRC_PREFIX}scenario/snapshot/${kind}/download`, {
      ...input,
      facility: facility === "ALL" ? undefined : facility,
    });
  } catch (err) {
    throw new Error(await blobErrorDetail(err, "Could not download the file."));
  }
  const match = result.contentDisposition ? /filename="?([^"]+)"?/.exec(result.contentDisposition) : null;
  const filename = match ? match[1] : `scenario-snapshot-${kind}.csv`;
  const blobUrl = window.URL.createObjectURL(result.blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

// ── Propose / Proposal Log ───────────────────────────────────────────────────────────────
// APPROVED/EXECUTED_CONFIRMED/DISCREPANCY are no longer a reviewer's choice: a reviewer's
// only action is APPROVED or REJECTED (useReviewScenarioPledgeProposal below). Once approved,
// EXECUTED_CONFIRMED vs DISCREPANCY is derived live from a real per-contract check against
// bankdwn_daily, computed on every read by scenario.py's _recompute_reconciliation. See
// useScenarioReconciliation for the per-contract detail behind that derivation.
export type ScenarioPledgeStatus =
  | "PENDING"
  | "APPROVED"
  | "EXECUTED_CONFIRMED"
  | "DISCREPANCY"
  | "REJECTED";

export interface ScenarioPledgeProposal {
  proposalId: string;
  scenarioRef: string;
  sourceInvestorCode: string;
  destinationInvestorCode: string;
  destinationFacilityCode: string | null;
  contractKeys: string[];
  dollarTotal: number;
  contractCount: number;
  matchedRuleSummary: string | null;
  reason: string;
  status: ScenarioPledgeStatus;
  /** A derived note for APPROVED/EXECUTED_CONFIRMED/DISCREPANCY (e.g. "Awaiting next
   *  reconciliation cycle"), shown instead of reviewNotes for those statuses -- see
   *  scenario.py's _reconciliation_note(). Null for PENDING/REJECTED, where reviewNotes (or
   *  its absence) is the whole story. */
  reconciliationNote: string | null;
  proposedByRole: string;
  proposedAt: string;
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  /** ACTIVE, or RETIRED once a request to retire the scenario this proposal belongs to has been approved. */
  scenarioStatus: ScenarioLifecycle;
}

export interface ScenarioPledgeDestinationInput {
  investorCode: string;
  facilityCode: string;
  contractKeys: string[];
  dollarTotal: number;
  contractCount: number;
  matchedRuleSummary?: string;
  reason: string;
}

/** A scenario is never deleted: it is ACTIVE or, once retiring it has been approved, RETIRED. */
export type ScenarioLifecycle = "ACTIVE" | "RETIRED";

function invalidateScenarioLog(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["prc-scenario-pledge-proposals"] });
  qc.invalidateQueries({ queryKey: ["prc-scenario-retire-requests"] });
  qc.invalidateQueries({ queryKey: ["prc-scenario-reconciliation"] });
  // the approved-scenarios list (Scenario Portfolio) is the approved subset of the same records
  qc.invalidateQueries({ queryKey: ["prc-scenario-approved"] });
}

export function useCreateScenarioPledgeProposals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sourceInvestorCode: string;
      scenarioRef?: string;
      /** The scenario's own inputs, stored with every destination's record so an approved scenario can be opened again with
       *  its investor code, date range, conditions and destinations already filled in. */
      dateFrom?: string;
      dateTo?: string;
      conditions?: ScenarioCondition[];
      /** The scenario started from an uploaded contract list (so it cannot be reopened from its window). */
      fromUpload?: boolean;
      /** How the pool was chosen. MANUAL (the default): investor codes + a booking window (dateFrom / dateTo), read on the effective date.
       *  UPLOAD: an uploaded contract list, read on the effective date; `poolKeys` is that list (what lets the scenario be reopened on the
       *  same contracts) and there is no booking window, so dateFrom / dateTo are left out. */
      sourceMode?: ScenarioSourceMode;
      /** The data file date the pool was actually read on (not the date asked for). */
      effectiveDate?: string;
      poolKeys?: string[];
      destinations: ScenarioPledgeDestinationInput[];
    }) =>
      postJson<{ scenarioRef: string; proposalIds: string[] }>(
        `${PRC_PREFIX}scenario/pledge-proposals`,
        input,
      ),
    onSuccess: () => invalidateScenarioLog(qc),
  });
}

export function useScenarioPledgeProposals(page: { limit: number; offset: number; status?: ScenarioPledgeStatus }) {
  return useQuery({
    queryKey: ["prc-scenario-pledge-proposals", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.status) q.set("status", page.status);
      return getJson<{ proposals: ScenarioPledgeProposal[]; total: number }>(
        `${PRC_PREFIX}scenario/pledge-proposals?${q.toString()}`,
      );
    },
  });
}

export function usePendingScenarioPledgeProposals() {
  return useScenarioPledgeProposals({ limit: 100, offset: 0, status: "PENDING" });
}

export function useReviewScenarioPledgeProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: "APPROVED" | "REJECTED";
      notes: string;
      reviewerRole?: string;
    }) =>
      postJson<{ ok: boolean; error?: string; decision?: string }>(
        `${PRC_PREFIX}scenario/pledge-proposals/${encodeURIComponent(input.proposalId)}/review`,
        { decision: input.decision, notes: input.notes, reviewerRole: input.reviewerRole },
      ),
    onSuccess: (_data, input) => {
      invalidateScenarioLog(qc);
      // Approving seeds reconciliation state for this proposal -- the Reconciliation tab
      // needs a fresh fetch too, not just the Log.
      if (input.decision === "APPROVED") {
        qc.invalidateQueries({ queryKey: ["prc-scenario-reconciliation"] });
      }
    },
  });
}

// ── Approved scenarios (Scenario Portfolio's "open an earlier scenario" option and its approved log) ──
export type ScenarioApprovedStatus = "APPROVED" | "EXECUTED_CONFIRMED" | "DISCREPANCY";

export interface ScenarioApprovedDestination {
  proposalId: string;
  investorCode: string;
  facilityCode: string | null;
  contractCount: number;
  dollarTotal: number;
  status: ScenarioPledgeStatus;
}

/** How a scenario's pool was chosen: investor codes + a booking window (MANUAL), or an uploaded contract list (UPLOAD). */
export type ScenarioSourceMode = "MANUAL" | "UPLOAD";

export interface ScenarioApproved {
  scenarioRef: string;
  /** Started from an uploaded contract list. */
  fromUpload?: boolean;
  /** How the pool was chosen. An UPLOAD scenario has an effective date and no booking window (dateFrom / dateTo are null); a MANUAL one has
   *  both a booking window and the effective date it was read on (null on one approved before the effective date was recorded). */
  sourceMode: ScenarioSourceMode;
  effectiveDate: string | null;
  /** UPLOAD: how many contracts of the uploaded list were saved. 0 on a scenario approved before they were kept: it cannot be reopened. */
  poolKeyCount: number;
  /** Named like the Reconciliation page's batches: "Sep 15 · Code 52 · CAP1" (several destinations: "Sep 15 · Code 52 · CAP1 + Code 53 · CAP2") */
  label: string;
  approvedAt: string | null;
  sourceInvestorCode: string;
  dateFrom: string | null;
  dateTo: string | null;
  /** The conditions as the builder held them, destinations included; null on scenarios approved before they were recorded. */
  conditions: ScenarioCondition[] | null;
  /** false on a scenario approved before its conditions were saved: it then opens on the default conditions plus its destinations. */
  conditionsRecorded: boolean;
  /** false when the date range was never recorded: listed in the log, but it cannot be opened again. */
  restorable: boolean;
  destinations: ScenarioApprovedDestination[];
  contractCount: number;
  dollarTotal: number;
  status: ScenarioApprovedStatus;
  /** ACTIVE, or RETIRED (kept in the tables and the log, but no longer reopenable). */
  scenarioStatus: ScenarioLifecycle;
  /** A request to retire this scenario is waiting for a decision. */
  retirePending: boolean;
}

export function useApprovedScenarios() {
  return useQuery({
    queryKey: ["prc-scenario-approved"],
    queryFn: () => getJson<{ scenarios: ScenarioApproved[] }>(`${PRC_PREFIX}scenario/approved`),
  });
}

// ── Scenario Reconciliation ──────────────────────────────────────────────────────────────
export interface ScenarioReconciliationBatch {
  proposalId: string;
  batchLabel: string;
  /** ISO date the proposal was approved -- for the date filter on the Reconciliation tab. */
  batchDate: string | null;
  facilityCode: string | null;
  unreconciledCount: number;
  totalCount: number;
  /** The scenario this batch belongs to (one scenario has one batch per destination). */
  scenarioRef: string;
  scenarioStatus: ScenarioLifecycle;
  retirePending: boolean;
}

export interface ScenarioReconciliationRow {
  proposalId: string;
  /** The matching identity (contract_schedule_key) -- for the row's React key only. Display
   *  contractNumber and scheduleNumber, never this, wherever a contract is shown to a person. */
  contractKey: string;
  /** The real, human Contract Number -- joined off the live snapshot server-side. */
  contractNumber: string;
  /** Its Schedule Number, read from the stored key (null only for a key that has none). */
  scheduleNumber: number | null;
  batchLabel: string;
  batchDate: string | null;
  financedAmount: number;
  approvedCode: string;
  currentCode: string | null;
  /** Present on unreconciled rows only: days since the proposal was approved. */
  daysUnreconciled?: number | null;
  /** Present on reconciled rows only: the first time this contract was observed matching --
   *  a real, stable fact (see prc_scenario_pledge_reconciliation), not a live "as of" label. */
  reconciledAt?: string;
}

export interface ScenarioReconciliationResult {
  batches: ScenarioReconciliationBatch[];
  unreconciled: ScenarioReconciliationRow[];
  reconciled: ScenarioReconciliationRow[];
}

/** `includeRetired`: also return the batches of retired scenarios (left out by default). */
export function useScenarioReconciliation(proposalId?: string, includeRetired = false) {
  return useQuery({
    queryKey: ["prc-scenario-reconciliation", proposalId ?? "all", includeRetired],
    queryFn: () => {
      const q = new URLSearchParams();
      if (proposalId) q.set("proposalId", proposalId);
      if (includeRetired) q.set("includeRetired", "true");
      const qs = q.toString();
      return getJson<ScenarioReconciliationResult>(`${PRC_PREFIX}scenario/reconciliation${qs ? `?${qs}` : ""}`);
    },
  });
}

// ── Retiring an approved scenario: a request, a decision, a log. Nothing is deleted. ─────────────────
export type ScenarioRetireStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface ScenarioRetireRequest {
  retireId: string;
  scenarioRef: string;
  scenarioLabel: string | null;
  reason: string;
  status: ScenarioRetireStatus;
  proposedByRole: string;
  proposedAt: string | null;
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

/** Ask for an approved scenario to be retired. It goes to a contributor for a decision; the scenario stays active until then. */
export function useCreateScenarioRetireRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { scenarioRef: string; reason: string }) =>
      postJson<{ retireId: string; scenarioRef: string }>(`${PRC_PREFIX}scenario/retire-proposals`, input),
    onSuccess: () => {
      invalidateScenarioLog(qc);
    },
  });
}

export function useScenarioRetireRequests(page: { limit: number; offset: number; status?: ScenarioRetireStatus }) {
  return useQuery({
    queryKey: ["prc-scenario-retire-requests", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.status) q.set("status", page.status);
      return getJson<{ requests: ScenarioRetireRequest[]; total: number }>(
        `${PRC_PREFIX}scenario/retire-proposals?${q.toString()}`,
      );
    },
  });
}

export function useReviewScenarioRetireRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { retireId: string; decision: "APPROVED" | "REJECTED"; notes: string; reviewerRole?: string }) =>
      postJson<{ ok: boolean; error?: string; decision?: string }>(
        `${PRC_PREFIX}scenario/retire-proposals/${encodeURIComponent(input.retireId)}/review`,
        { decision: input.decision, notes: input.notes, reviewerRole: input.reviewerRole },
      ),
    onSuccess: () => {
      invalidateScenarioLog(qc);
    },
  });
}

/** The message of a failed call: the server's own `detail` where there is one. */
export function apiDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Fetches the CSV through axios (so the global Keycloak-bearer interceptor in
 *  src/index.js actually attaches, unlike a plain `<a href>` navigation, which is a bare
 *  browser GET the interceptor never sees) and saves it client-side. This endpoint requires
 *  an authenticated Athena session same as every other PRC call; a raw href to it answers
 *  "Missing bearer token" instead of downloading. */
export async function downloadScenarioPledgeProposal(proposalId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const serverConfig = require("../../../config/server.js");
  const base: string = serverConfig.api_base_url;
  const url = `${base}${PRC_PREFIX}scenario/pledge-proposals/${encodeURIComponent(proposalId)}/download`;

  const res = await axios.get(url, { responseType: "blob" });
  const disposition: string | undefined = res.headers?.["content-disposition"];
  const match = disposition ? /filename="?([^"]+)"?/.exec(disposition) : null;
  const filename = match ? match[1] : `${proposalId}.csv`;

  const blobUrl = window.URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}
